// Mirror of apps/api/src/lib/time-slot-generator.ts. Duplicated client-side so
// the admin page can preview the slot grid as periods are edited without a
// round-trip. Keep in sync with the server when the generator changes.

export interface SlotPeriod {
  startTime: string;
  durationMinutes: number;
}

export interface GenerateSlotsInput {
  courtWorkingHoursStart: string;
  courtWorkingHoursEnd: string;
  periods: SlotPeriod[] | null;
  defaultDurationMinutes: number;
}

export interface GeneratedSlot {
  startTime: string;
  durationMinutes: number;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function toHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

export function generateSlotsFromPeriods(
  input: GenerateSlotsInput,
): GeneratedSlot[] {
  const courtStartMin = toMinutes(input.courtWorkingHoursStart);
  const courtEndMin = toMinutes(input.courtWorkingHoursEnd);

  const effectivePeriods: SlotPeriod[] =
    input.periods && input.periods.length > 0
      ? [...input.periods].sort(
          (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime),
        )
      : [
          {
            startTime: input.courtWorkingHoursStart,
            durationMinutes: input.defaultDurationMinutes,
          },
        ];

  const slots: GeneratedSlot[] = [];
  for (let i = 0; i < effectivePeriods.length; i++) {
    const period = effectivePeriods[i];
    const periodStartMin = Math.max(toMinutes(period.startTime), courtStartMin);
    const next = effectivePeriods[i + 1];
    const periodEndMin = Math.min(
      next ? toMinutes(next.startTime) : courtEndMin,
      courtEndMin,
    );
    if (periodEndMin <= periodStartMin) continue;
    if (period.durationMinutes <= 0) continue;
    let current = periodStartMin;
    while (current + period.durationMinutes <= periodEndMin) {
      slots.push({
        startTime: toHHMM(current),
        durationMinutes: period.durationMinutes,
      });
      current += period.durationMinutes;
    }
  }
  return slots;
}
